import {globalError} from "modules/actions/errors/";
import {
	checkGeo,
	checkGeoSuccess,
	checkTerms,
	checkTermsFailed,
	checkTermsSuccess,
	checkUserName,
	checkUserNameSuccess,
	fetchFavoritePlayers,
	fetchFavoritePlayersFailed,
	fetchFavoritePlayersSuccess,
	fetchFavoriteSquadsFailed,
	fetchFavoriteSquadsSuccess,
	getCountry,
	getCountrySuccess,
	getSessionUser,
	postLoginViaBackDoor,
	postUserLogoutSuccess,
	retrieveUserSuccess,
	setGameMessageViewState,
	setSecondTutorialViewState,
	setSessionState,
	saveTooltipInsightsViewState,
	setTooltipModalState,
	setTutorialViewState,
	userLogin,
	acceptTerms,
	fetchUserRewards,
	fetchUserRewardsSuccess,
	setViewRewardModalSuccess,
	setViewRewardModal,
	setBannerViewState,
} from "modules/actions/user";
import {RequestState} from "modules/enums";
import {getSentryUser} from "modules/selectors";
import Api from "modules/utils/Api";
import ApiError from "modules/utils/Api/ApiError";
import {redirectToMlbLoginForm, removeGameReloaded} from "modules/utils/mlbConfig";
import {captureSentryError} from "modules/utils/sentry";
import {select} from "typed-redux-saga";
import {call, delay, put} from "typed-redux-saga/macro";

export const processWithBackdoorSaga = function* (action: ReturnType<typeof postLoginViaBackDoor>) {
	try {
		const result = yield* call(Api.Auth.backdoor, action.payload);

		yield* put(retrieveUserSuccess(result.data.success));
		removeGameReloaded();
	} catch (e) {
		yield* put(globalError(e));
	}
};

export const processSessionSaga = function* (_action: ReturnType<typeof getSessionUser>) {
	yield* put(setSessionState(RequestState.Requested));

	try {
		const result = yield* call(Api.User.session);
		yield* put(retrieveUserSuccess(result.data.success));
		removeGameReloaded();
	} catch (e) {
		console.log(e);
	}

	yield* put(setSessionState(RequestState.Received));
};

export const enableMulliganSaga = function* () {
	try {
		const result = yield* call(Api.User.enable_mulligan);
		ApiError.CHECK(result.data);
	} catch (e) {
		yield* put(globalError(e));
	}
};

export const processLogoutSaga = function* () {
	try {
		const result = yield* call(Api.Auth.logout);
		ApiError.CHECK(result.data);
		yield* put(postUserLogoutSuccess());
	} catch (e) {
		yield* put(globalError(e));
	}
};
export const processLogoutSagaDebug = function* () {
	try {
		const result = yield* call(Api.Auth.logout);
		ApiError.CHECK(result.data);
	} catch (e) {
		yield* put(globalError(e));
	}
};

export const processLoginSaga = function* (action: ReturnType<typeof userLogin>) {
	try {
		const result = yield* call(Api.Auth.login, action.payload);

		ApiError.CHECK(result.data);

		yield* put(retrieveUserSuccess(result.data.success));

		removeGameReloaded();
	} catch (e) {
		yield* put(globalError(e));
		yield* delay(5000);
		redirectToMlbLoginForm();
	}
};

export const userLoginWithAcceptTermsSaga = function* (action: ReturnType<typeof userLogin>) {
	try {
		const result = yield* call(Api.Auth.login, action.payload);

		ApiError.CHECK(result.data);

		yield* put(retrieveUserSuccess(result.data.success));
		yield* put(acceptTerms());

		removeGameReloaded();
	} catch (e) {
		yield* put(globalError(e));
		yield* delay(5000);
		redirectToMlbLoginForm();
	}
};

export const checkTermsSaga = function* (action: ReturnType<typeof checkTerms>) {
	try {
		const result = yield* call(Api.User.check_terms, action.payload.uid);

		ApiError.CHECK(result.data);

		yield* put(checkTermsSuccess(result.data.success));
	} catch (e) {
		yield* put(checkTermsFailed());
		const user = yield* select(getSentryUser);
		captureSentryError("fetchFriendPicksByPlayerSaga", action, e, user);
		yield* put(globalError(e));
	}
};

export const acceptTermsSaga = function* () {
	try {
		const result = yield* call(Api.User.accept_terms);

		ApiError.CHECK(result.data);

		yield* put(retrieveUserSuccess(result.data.success));
	} catch (e) {
		yield* put(globalError(e));
	}
};

export const checkUserNameSaga = function* ({payload}: ReturnType<typeof checkUserName>) {
	try {
		const result = yield* call(Api.User.check_username, payload.uid);

		ApiError.CHECK(result.data);

		if (result?.data.success?.user?.username) {
			yield* put(checkUserNameSuccess(result.data.success));
		} else {
			// redirectToMlbLoginOnBoarding();
			redirectToMlbLoginForm();
		}
	} catch (e) {
		// redirectToMlbLoginOnBoarding();
		redirectToMlbLoginForm();
	}
};

export const checkGeoSaga = function* (_action: ReturnType<typeof checkGeo>) {
	try {
		const result = yield* call(Api.User.check_country);

		ApiError.CHECK(result.data);

		yield* put(checkGeoSuccess(result.data.success || {isAllowed: true}));
	} catch (e) {
		yield* put(globalError(e));
	}
};

export const getCountrySaga = function* (_action: ReturnType<typeof getCountry>) {
	try {
		const result = yield* call(Api.User.country);

		ApiError.CHECK(result.data);

		yield* put(getCountrySuccess(result.data.success || {isoCode: ""}));
	} catch (e) {
		yield* put(globalError(e));
	}
};

export const fetchFavoritePlayersSaga = function* (
	_action: ReturnType<typeof fetchFavoritePlayers>
) {
	try {
		const result = yield* call(Api.User.favorite_players);

		ApiError.CHECK(result.data);

		yield* put(fetchFavoritePlayersSuccess(result.data.success));
	} catch (e) {
		yield* put(fetchFavoritePlayersFailed());
		yield* put(globalError(e));
	}
};

export const fetchFavoriteSquadsSaga = function* (
	_action: ReturnType<typeof fetchFavoritePlayers>
) {
	try {
		const result = yield* call(Api.User.favorite_squads);

		ApiError.CHECK(result.data);

		yield* put(
			fetchFavoriteSquadsSuccess({favoriteSquadIds: result.data.success.favoriteSquadIds})
		);
	} catch (e) {
		yield* put(fetchFavoriteSquadsFailed());
		yield* put(globalError(e));
	}
};

export const updateBannerViewStateSaga = function* ({
	payload,
}: ReturnType<typeof setBannerViewState>) {
	try {
		const result = yield* call(Api.User.view_banner, payload);

		ApiError.CHECK(result.data);

		yield* put(retrieveUserSuccess(result.data.success));
	} catch (e) {
		yield* put(globalError(e));
	}
};

export const updateTutorialViewStateSaga = function* (
	_action: ReturnType<typeof setTutorialViewState>
) {
	try {
		const result = yield* call(Api.User.view_tutorial);

		ApiError.CHECK(result.data);

		yield* put(retrieveUserSuccess(result.data.success));
	} catch (e) {
		yield* put(globalError(e));
	}
};

export const updateSecondTutorialViewStateSaga = function* (
	_action: ReturnType<typeof setSecondTutorialViewState>
) {
	try {
		const result = yield* call(Api.User.view_second_tutorial);

		ApiError.CHECK(result.data);

		yield* put(retrieveUserSuccess(result.data.success));
	} catch (e) {
		yield* put(globalError(e));
	}
};

export const updateGameMessageViewStateSaga = function* (
	_action: ReturnType<typeof setGameMessageViewState>
) {
	try {
		const result = yield* call(Api.User.view_game_message);

		ApiError.CHECK(result.data);

		yield* put(retrieveUserSuccess(result.data.success));
	} catch (e) {
		yield* put(globalError(e));
	}
};

export const updateTooltipViewStateSaga = function* (
	_action: ReturnType<typeof setTooltipModalState>
) {
	try {
		const result = yield* call(Api.User.view_tooltip);

		ApiError.CHECK(result.data);

		yield* put(retrieveUserSuccess(result.data.success));
	} catch (e) {
		yield* put(globalError(e));
	}
};

export const updateTooltipInsightsViewStateSaga = function* (
	_action: ReturnType<typeof saveTooltipInsightsViewState>
) {
	try {
		const response = yield* call(Api.User.view_bubble_message);

		ApiError.CHECK(response.data);

		yield* put(retrieveUserSuccess(response.data.success));
	} catch (e) {
		yield* put(globalError(e));
	}
};

export const fetchUserRewardsSaga = function* (_action: ReturnType<typeof fetchUserRewards>) {
	try {
		const response = yield* call(Api.Reward.show_my);

		ApiError.CHECK(response.data);

		yield* put(fetchUserRewardsSuccess(response.data.success));
	} catch (e) {
		yield* put(globalError(e));
	}
};
export const setViewRewardModalSaga = function* (_action: ReturnType<typeof setViewRewardModal>) {
	try {
		const response = yield* call(Api.User.view_reward_modal);

		ApiError.CHECK(response.data);

		yield* put(setViewRewardModalSuccess(response.data.success));
	} catch (e) {
		yield* put(globalError(e));
	}
};
